import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormSection,
  Input,
} from '@/components/ui';

export type AdjustDirection = 'grant' | 'deduct';

/** 마일리지/예치금 공용 수동 조정 다이얼로그. 예치금은 reason 라벨이 "거래 설명(title)" 으로 쓰인다. */
export interface ManualAdjustDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  direction: AdjustDirection;
  /** 'mileage' 는 reason + memo, 'deposit' 은 title 만 (BE DTO 정합). */
  variant: 'mileage' | 'deposit';
  busy?: boolean;
  /** 선택된 회원 표시명 (UUID 미노출). */
  memberLabel: string;
  onSubmit: (input: { amount: number; reason: string; memo?: string }) => void;
}

const TITLE: Record<AdjustDirection, string> = {
  grant: '수동 지급',
  deduct: '수동 차감',
};

export const ManualAdjustDialog = ({
  open,
  onOpenChange,
  direction,
  variant,
  busy,
  memberLabel,
  onSubmit,
}: ManualAdjustDialogProps) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setAmount('');
      setReason('');
      setMemo('');
      setError(null);
    }
  }, [open]);

  const reasonLabel = variant === 'deposit' ? '거래 설명' : '사유';

  const handleSubmit = () => {
    setError(null);
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('금액은 1 이상의 정수여야 합니다.');
      return;
    }
    if (reason.trim() === '') {
      setError(`${reasonLabel}을(를) 입력하세요.`);
      return;
    }
    onSubmit({
      amount: Math.floor(parsed),
      reason: reason.trim(),
      ...(variant === 'mileage' && memo.trim() ? { memo: memo.trim() } : {}),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{TITLE[direction]}</DialogTitle>
          <DialogDescription>
            대상 회원: <span style={{ color: 'var(--content-strong)' }}>{memberLabel}</span>
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <div
            className="rounded-md p-2.5 text-[12px]"
            role="alert"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            {error}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <FormSection density="compact" label="금액" required>
            <Input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-label="금액"
            />
          </FormSection>
          <FormSection density="compact" label={reasonLabel} required>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              required
              aria-label={reasonLabel}
            />
          </FormSection>
          {variant === 'mileage' ? (
            <FormSection density="compact" label="메모 (선택)">
              <Input
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                maxLength={500}
                aria-label="메모"
              />
            </FormSection>
          ) : null}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              취소
            </Button>
          </DialogClose>
          <Button
            variant={direction === 'deduct' ? 'danger' : 'primary'}
            size="sm"
            disabled={busy}
            onClick={handleSubmit}
            data-testid="manual-adjust-submit"
          >
            {TITLE[direction]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
